/* =====================================================================
 * GuideSiblings — 문서 끝의 이전 / 다음 문서 카드.
 *
 * 문서가 있는 페이지(GuideArticle)와 아직 준비 중인 페이지가 같은 카드를 쓴다.
 * 넘어갈 문서가 아직 준비 전이면 카드에 "준비 중" 을 붙여, 누르기 전에 알 수 있게 한다.
 * ===================================================================== */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import * as S from './GuideArticle.styles';

export interface GuideSibling {
  readonly label: string;
  readonly onSelect: () => void;
  /** 아직 문서가 없는 항목. */
  readonly comingSoon?: boolean;
}

export interface GuideSiblingsProps {
  readonly prev?: GuideSibling;
  readonly next?: GuideSibling;
}

export const GuideSiblings: React.FC<GuideSiblingsProps> = ({ prev, next }) => {
  const { t } = useTranslation();
  if (!prev && !next) return null;

  const label = (sibling: GuideSibling) => (
    <S.SiblingLabel>
      {sibling.label}
      {sibling.comingSoon && <S.SiblingBadge>{t('userGuide.comingSoon')}</S.SiblingBadge>}
    </S.SiblingLabel>
  );

  return (
    <S.Siblings aria-label="다른 문서">
      {prev ? (
        <S.SiblingLink type="button" onClick={prev.onSelect}>
          <S.SiblingHint>
            <ArrowLeft size={13} /> 이전
          </S.SiblingHint>
          {label(prev)}
        </S.SiblingLink>
      ) : (
        <span />
      )}
      {next && (
        <S.SiblingLink type="button" $align="end" onClick={next.onSelect}>
          <S.SiblingHint>
            다음 <ArrowRight size={13} />
          </S.SiblingHint>
          {label(next)}
        </S.SiblingLink>
      )}
    </S.Siblings>
  );
};
